/* eslint-disable vue/component-definition-name-casing */
import error from '@/utils/mixins/error';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import 'core-js/stable';
// Registers the <mcs-3d-viewer> custom element (prebuilt, Vue-free bundle).
import 'mcs-3d-viewer';
import PortalVue from 'portal-vue';
import 'regenerator-runtime/runtime';
import VueMask from 'v-mask';
import Vue from 'vue';
import VueRouter from 'vue-router';
import httpClient from './api/httpClient';
import vuetify from './plugins/vuetify';
import './plugins/vuetify-mask';
import router from './router/index';
import mixpanelService from './services/mixpanelService';
import store from './store';
import dateFilter from './utils/filters/date';
import einFilter from './utils/filters/ein';
import numeralFilter from './utils/filters/numeral';
import stringFilter from './utils/filters/string';
import formValidationRules from './utils/formValidationRules';
import general from './utils/mixins/general';

// import mcs core components
import McsAbout from './components/_core/mcs-about';
import McsActionCard from './components/_core/mcs-action-card';
import McsAlert from './components/_core/mcs-alert';
import McsAvatar from './components/_core/mcs-avatar';
import McsBtn from './components/_core/mcs-btn';
import McsCard from './components/_core/mcs-card';
import McsChip from './components/_core/mcs-chip';
import McsChipSelect from './components/_core/mcs-chip-select';
import McsDatePicker from './components/_core/mcs-date-picker';
import McsIconList from './components/_core/mcs-icon-list';
import McsImageCard from './components/_core/mcs-image-card';
import McsInfoCard from './components/_core/mcs-info-card';
import McsInlineNav from './components/_core/mcs-inline-nav';
import McsInputLabel from './components/_core/mcs-input-label';
import McsOutlinedInput from './components/_core/mcs-outlined-input';
import McsPageNav from './components/_core/mcs-page-nav';
import McsReportSummaryCard from './components/_core/mcs-report-summary-card';
import McsSheet from './components/_core/mcs-sheet';
import McsTableCard from './components/_core/mcs-table-card';
import McsToast from './components/_core/mcs-toast';

// import root components
import { mapGetters } from 'vuex';
import App from './App';

// attach some 'global' (non reactive) components
Vue.prototype.$eventBus = new Vue();
Vue.prototype.$http = axios;
Vue.prototype.$mixpanel = mixpanelService;
Vue.prototype.$rules = formValidationRules;

// register mcs core components
Vue.component('mcs-about', McsAbout);
Vue.component('mcs-alert', McsAlert);
Vue.component('mcs-avatar', McsAvatar);
Vue.component('mcs-btn', McsBtn);
Vue.component('mcs-card', McsCard);
Vue.component('mcs-chip', McsChip);
Vue.component('mcs-chip-select', McsChipSelect);
Vue.component('mcs-date-picker', McsDatePicker);
Vue.component('mcs-action-card', McsActionCard);
Vue.component('mcs-icon-list', McsIconList);
Vue.component('mcs-image-card', McsImageCard);
Vue.component('mcs-info-card', McsInfoCard);
Vue.component('mcs-inline-nav', McsInlineNav);
Vue.component('mcs-input-label', McsInputLabel);
Vue.component('mcs-outlined-input', McsOutlinedInput);
Vue.component('mcs-page-nav', McsPageNav);
Vue.component('mcs-report-summary-card', McsReportSummaryCard);
Vue.component('mcs-sheet', McsSheet);
Vue.component('mcs-table-card', McsTableCard);
Vue.component('mcs-toast', McsToast);

// add filters
Vue.filter('regularDate', dateFilter.regularDate);
Vue.filter('regularDateNoYear', dateFilter.regularDateNoYear);
Vue.filter('regularDateDowNoYear', dateFilter.regularDateDowNoYear);
Vue.filter('regularDateWithTime', dateFilter.regularDateWithTime);
Vue.filter('normalDate', dateFilter.normalDate);
Vue.filter('normalDateWithTime', dateFilter.normalDateWithTime);
Vue.filter('longDate', dateFilter.longDate);
Vue.filter('longDateWithTime', dateFilter.longDateWithTime);
Vue.filter('shortDateWithTime', dateFilter.shortDateWithTime);
Vue.filter('time', dateFilter.time);
Vue.filter('year', dateFilter.year);
Vue.filter('monthYear', dateFilter.monthYear);
Vue.filter('oneDecimal', numeralFilter.oneDecimal);
Vue.filter('noDecimal', numeralFilter.noDecimal);
Vue.filter('noDecimalAbs', numeralFilter.noDecimalAbs);
Vue.filter('lower', stringFilter.lower);
Vue.filter('upper', stringFilter.upper);
Vue.filter('capitalize', stringFilter.capitalize);
Vue.filter('separator', numeralFilter.separator);
Vue.filter('currency', numeralFilter.currency);
Vue.filter('currencyShort', numeralFilter.currencyShort);
Vue.filter('currencyBig', numeralFilter.currencyBig);
Vue.filter('currencySmall', numeralFilter.currencySmall);
Vue.filter('percentage', numeralFilter.percentage);
Vue.filter('twoDecimals', numeralFilter.twoDecimals);
Vue.filter('year', dateFilter.year);
Vue.filter('monthDate', dateFilter.monthDate);
Vue.filter('ein', einFilter.ein);

// final configurations
Vue.use(VueRouter);
Vue.use(PortalVue);
Vue.use(VueMask);
Vue.config.productionTip = false;
Vue.config.devtools = true;
Vue.config.ignoredElements = ['mcs-checkout', 'mcs-checkout-subscription', 'mcs-designs-rail', 'mcs-3d-viewer'];

Vue.mixin(error);
Vue.mixin(general);

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_URL,
  environment: process.env.NODE_ENV,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router)
    })
  ],
  denyUrls: [/mcsdev.com/i, /extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],
  ignoreErrors: [
    // Expected when the user is not authorized for the requested URL —
    // the app uses this 403 as a signal, not as an error.
    /auth\/authorize.*Request failed with status code 403/i,
    // vue-router navigation failures — expected when a user interrupts an
    // in-progress navigation, a guard aborts/redirects, or push() targets the
    // current route. Not actionable; they bubble up via onunhandledrejection.
    /^Navigation cancelled from /,
    /^Navigation aborted from /,
    /^Redirected when going from /,
    /^Avoided redundant navigation to current location/
  ],
  tracesSampleRate: process.env.VUE_APP_IN_DEV === 'true' ? 1.0 : 0.1,
  tracePropagationTargets: [process.env.VUE_APP_DASHBOARD_URL, process.env.VUE_APP_API_URL]
});

/* eslint-disable no-new */
new Vue({
  vuetify,
  router,
  store,
  mixins: [],

  computed: {
    pageLoading() {
      return this.$store.state.config.pageLoading;
    },

    username() {
      return this.$store.state.user.username;
    },

    ...mapGetters({ master_account: 'user/master_account' })
  },

  watch: {
    pageLoading(val) {
      if (val) {
        window.loadingTimeout = setTimeout(() => {
          this.$store.commit('config/SET_BLOCK_UI_LOADING', true);
        }, 2000);
      } else {
        if (window.loadingTimeout) window.clearTimeout(window.loadingTimeout);
        this.$store.commit('config/SET_BLOCK_UI_LOADING', false);
      }
    },

    username(val) {
      Sentry.setUser({ email: val, id: this.$store.state.user.userId });
    }
  },

  created() {
    Vue.prototype.$api = httpClient;
    Vue.prototype.$portalMode = () => {
      return this.master_account.enablePortal;
    };
    this.$mixpanel.initialize(
      this.$store.state.config.mixpanelConfig.token,
      this.$store.state.config.mixpanelConfig.proxyDomain
    );
  },

  methods: {
    setPageLoading(val) {
      this.$store.commit('config/SET_PAGE_LOADING', val);
    }
  },
  render: (h) => h(App)
}).$mount('#app');
